import { create } from 'zustand';
import { screenSizes } from '../constants/constants';

const STORAGE_KEY = 'debug-overlays';

// Every toggleable diagnostic layer drawn over the exercise, keyed by the name
// used in localStorage. Adding one here is enough for it to appear in the pause
// modal's list and to be persisted. Label/description text lives in the
// `exercise` translation namespace under `debugOverlaysSection.overlays.<key>`,
// looked up by key in DebugOverlaysSection rather than stored here.
export const DEBUG_OVERLAYS = [{ key: 'score' }, { key: 'holdTelemetry' }, { key: 'aspectRatio' }];

// Phones/small tablets have no room to spare over the video, so every layer
// starts hidden there. Read once at module load: a stored preference always wins
// afterwards, so a user who turns something on keeps it on their phone too.
const isSmallViewport = () => typeof window !== 'undefined' && window.innerWidth < screenSizes.lg;

const buildDefaults = () => {
  const isVisibleByDefault = !isSmallViewport();
  return DEBUG_OVERLAYS.reduce((acc, overlay) => {
    acc[overlay.key] = isVisibleByDefault;
    return acc;
  }, {});
};

const readStoredOverlays = () => {
  const defaults = buildDefaults();

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return defaults;
    }

    const parsed = JSON.parse(stored);
    if (parsed === null || typeof parsed !== 'object') {
      return defaults;
    }

    // Merge rather than replace, so a layer added after the user's preferences
    // were written still picks up its default instead of coming back undefined.
    return DEBUG_OVERLAYS.reduce((acc, overlay) => {
      acc[overlay.key] =
        typeof parsed[overlay.key] === 'boolean' ? parsed[overlay.key] : defaults[overlay.key];
      return acc;
    }, {});
  } catch (error) {
    console.error('Failed to read stored debug overlay preferences:', error);
    return defaults;
  }
};

const persistOverlays = overlays => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(overlays));
  } catch (error) {
    console.error('Failed to persist debug overlay preferences:', error);
  }
};

export const useDebugOverlays = create(set => ({
  overlays: readStoredOverlays(),
  setOverlay: (key, value) =>
    set(state => {
      const overlays = { ...state.overlays, [key]: Boolean(value) };
      persistOverlays(overlays);
      return { overlays };
    }),
  toggleOverlay: key =>
    set(state => {
      const overlays = { ...state.overlays, [key]: !state.overlays[key] };
      persistOverlays(overlays);
      return { overlays };
    }),
}));
